/**
 * ViewerPermissionsModal Component
 *
 * Modal for managing which reports a viewer can access.
 * Lists all reports in the viewer's client with checkboxes.
 */
import { useState, useEffect, useCallback } from 'react';
import { Modal } from '../ui';
import { useAdmin } from '../../contexts/AdminContext';
import type { User } from '../../types';

interface ViewerPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  viewer: User | null;
}

export const ViewerPermissionsModal = ({
  isOpen,
  onClose,
  viewer,
}: ViewerPermissionsModalProps) => {
  const {
    reports,
    getViewerPermissions,
    updateViewerPermissions,
  } = useAdmin();

  const [selectedReportIds, setSelectedReportIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get reports for the viewer's client
  const clientReports = viewer
    ? reports.filter((r) => r.clientId === viewer.clientId)
    : [];

  // Load current permissions when modal opens
  useEffect(() => {
    if (isOpen && viewer) {
      setIsLoading(true);
      getViewerPermissions(viewer.id)
        .then((permissions) => {
          setSelectedReportIds(new Set(permissions));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, viewer, getViewerPermissions]);

  // Toggle single report
  const toggleReport = (reportId: string) => {
    setSelectedReportIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  // Select all reports
  const selectAll = () => {
    setSelectedReportIds(new Set(clientReports.map((r) => r.id)));
  };

  // Clear all selections
  const clearAll = () => {
    setSelectedReportIds(new Set());
  };

  // Save permissions
  const handleSave = useCallback(async () => {
    if (!viewer) return;

    setIsSaving(true);
    try {
      const success = await updateViewerPermissions(
        viewer.id,
        Array.from(selectedReportIds)
      );
      if (success) {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  }, [viewer, selectedReportIds, updateViewerPermissions, onClose]);

  if (!viewer) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Report Permissions - ${viewer.firstName} ${viewer.lastName}`}
      size="md"
    >
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          Select which reports this viewer can access.
        </p>
      </div>

      {/* Quick actions */}
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={selectAll}
          disabled={isLoading || isSaving}
          className="px-3 py-1 text-xs font-medium text-metrica-green border border-metrica-green rounded hover:bg-metrica-green/10 disabled:opacity-50"
        >
          Select All
        </button>
        <button
          type="button"
          onClick={clearAll}
          disabled={isLoading || isSaving}
          className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
        >
          Clear All
        </button>
      </div>

      {/* Reports list */}
      <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <svg
              className="animate-spin h-6 w-6 text-metrica-green"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          </div>
        ) : clientReports.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No reports available for this client.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {clientReports.map((report) => (
              <li key={report.id}>
                <label className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedReportIds.has(report.id)}
                    onChange={() => toggleReport(report.id)}
                    disabled={isSaving}
                    className="h-4 w-4 text-metrica-green border-gray-300 rounded focus:ring-metrica-green"
                  />
                  <span className="text-sm text-gray-900">{report.name}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Summary */}
      <div className="mt-4 text-sm text-gray-500">
        {selectedReportIds.size} of {clientReports.length} reports selected
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading || isSaving}
          className="px-4 py-2 text-sm font-medium text-white bg-metrica-green rounded-lg hover:bg-metrica-green/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-metrica-green disabled:opacity-50"
        >
          {isSaving ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Saving...
            </span>
          ) : (
            'Save Permissions'
          )}
        </button>
      </div>
    </Modal>
  );
};

export default ViewerPermissionsModal;
